type BadgeColor = "green" | "red" | "gray" | "yellow" | "blue";

export const ORDER_PAYMENT_STATUS: Record<string, { text: string; color: BadgeColor }> = {
  unpaid: { text: "Unpaid", color: "yellow" },
  // Legacy name for "unpaid" on orders saved before the payment ledger.
  pending: { text: "Unpaid", color: "yellow" },
  partially_paid: { text: "Partially paid", color: "blue" },
  paid: { text: "Paid", color: "green" },
};

export const REFUND_STATUS: Record<string, { text: string; color: BadgeColor }> = {
  pending: { text: "Initiated", color: "blue" },
  processed: { text: "Refunded", color: "green" },
  failed: { text: "Failed", color: "red" },
};

export const LINK_STATUS: Record<string, { text: string; color: BadgeColor }> = {
  active: { text: "Link active", color: "blue" },
  partially_paid: { text: "Link partially paid", color: "blue" },
  paid: { text: "Link paid", color: "green" },
  expired: { text: "Payment link expired", color: "red" },
  cancelled: { text: "Link cancelled", color: "gray" },
};

// What an order's payment badge should say in lists — an expired link with
// money still owed is the thing staff need to chase, so it wins.
export const orderPaymentBadge = (order: any): { text: string; color: BadgeColor } | null => {
  if (order?.orderStatus === "cancelled") return null;
  if (order?.balanceDue > 0 && order?.paymentLink?.status === "expired") {
    return { text: "Link expired", color: "red" };
  }
  return ORDER_PAYMENT_STATUS[order?.paymentStatus] || ORDER_PAYMENT_STATUS.unpaid;
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  card: "Card",
  upi: "UPI",
  netbanking: "Netbanking",
  wallet: "Wallet",
  bank_transfer: "Bank transfer",
  cheque: "Cheque",
  emi: "EMI",
  other: "Other",
};

// "Paid via" as reported by the gateway: UPI app, wallet, bank or card
// network. Unknown values (other banks/wallets) fall back to a readable form.
const PAYMENT_VIA_LABELS: Record<string, string> = {
  gpay: "GPay",
  phonepe: "PhonePe",
  paytm: "Paytm",
  amazonpay: "Amazon Pay",
  bhim: "BHIM",
  whatsapp: "WhatsApp Pay",
  upi_other: "Other UPI app",
  mobikwik: "MobiKwik",
  freecharge: "Freecharge",
  visa: "Visa",
  mastercard: "Mastercard",
  rupay: "RuPay",
  amex: "Amex",
  american_express: "Amex",
  diners_club: "Diners Club",
};

export const viaLabel = (via: string) =>
  PAYMENT_VIA_LABELS[via] ||
  // Short codes are bank codes (hdfc, icic, sbin...) — show them upper-case.
  (via.length <= 5
    ? via.toUpperCase()
    : via.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));

// "UPI · GPay" when the gateway told us the app/bank/card, else the method.
export const paymentMethodLabel = (payment: { method: string; via?: string }) => {
  const method = PAYMENT_METHOD_LABELS[payment.method] || payment.method;
  return payment.via ? `${method} · ${viaLabel(payment.via)}` : method;
};

export const formatExpiryMinutes = (minutes: number) => {
  if (minutes < 60) return `${minutes} minutes`;
  if (minutes < 1440) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const days = minutes / 1440;
  return `${days} day${days === 1 ? "" : "s"}`;
};

// "in 42 min" / "in 5 h" style countdown for an expiry timestamp.
export const timeUntil = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const minutes = Math.ceil(ms / 60000);
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `in ${hours} h ${minutes % 60} min`;
  return `in ${Math.floor(hours / 24)} days`;
};

export const whatsappShareUrl = (mobile: string | undefined, text: string) => {
  const digits = (mobile || "").replace(/\D/g, "");
  // Bare 10-digit Indian numbers need the country code for wa.me.
  const phone = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
};
